import * as Request from '../request';

// stored values of files
let storedAiJson;
let storedBoundingBoxes;
let storedBoundsPolygons;
let storedEntities;
let storedStyle;
let storedTheme;
let storedVenue;
let storedOrdinals;
let storedStructures;
let storedLevels;

let storedVenueId;

// stored paths of files to read from
let aiJsonPath;
let boundingBoxesPath;
let boundsPolygonsPath;
let entitiesPath;
let geojsonBasePath;
let stylePath;
let themePath;
let venuePath;
let aiVenuePath;
let imdfVenuePath;
let ordinalsPath;
let structuresPath;
let levelsPath;

/*
 * Requests can fetch these files that can vary based on user input
 * localFiles = {
 *     aiJson: getAiJson, getGeoJson
 *     boundsPolygons: getBoundsPolygons
 *     theme: getContent, getVenueCategories
 *     style: getContent
 *     venueData: getVenue
 *     entities: listPois
 * }
 *
 * Requests that are mocked
 * createPoiRequest                  // mocked
 * getContentVenueAccountTreeRequest // mocked
 * listContentRequest                // mocked
 * listVenuesRequest                 // mocked
 * openLocationRequest               // mocked
 * updatePoisRequest                 // mocked
 *
 */

/*
 * These are the file paths for which the mock data grabs data from
 */
const setFilePaths = filePaths => {
    aiJsonPath = filePaths.aiJsonPath;
    boundingBoxesPath = filePaths.boundingBoxesPath;
    boundsPolygonsPath = filePaths.boundsPolygonsPath;
    entitiesPath = filePaths.entitiesPath;
    geojsonBasePath = filePaths.geojsonBasePath;
    stylePath = filePaths.stylePath;
    themePath = filePaths.themePath;
    venuePath = filePaths.venuePath;
    ordinalsPath = filePaths.ordinalsPath;
    structuresPath = filePaths.structuresPath;
    levelsPath = filePaths.levelsPath;
    aiVenuePath = filePaths.aiVenuePath;
    imdfVenuePath = filePaths.imdfVenuePath;
};

const possiblyChangeVenue = venueId => {
    if (venueId !== storedVenueId) {
        storedVenueId = venueId;

        storedAiJson = undefined;
        storedBoundingBoxes = undefined;
        storedBoundsPolygons = undefined;
        storedEntities = undefined;
        storedStyle = undefined;
        storedTheme = undefined;
        storedVenue = undefined;
        storedOrdinals = undefined;
        storedStructures = undefined;
        storedLevels = undefined;
    }
};

const mockGetAiJson = async venueId => {
    if (!storedAiJson) {
        let aiJson = await Request.readJson(aiJsonPath);
        aiJson = JSON.stringify(aiJson).replace(/VENUEID/g, venueId); // VENUEID hack
        storedAiJson = JSON.parse(aiJson);
    }
    return storedAiJson;
};

const mockGetGeoJson = async (venueId, levelId) => {
    return Request.readJson(`${geojsonBasePath}/${levelId}.json`);
};

const mockGetBoundingBoxes = async () => {
    if (!storedBoundingBoxes) storedBoundingBoxes = await Request.readJson(boundingBoxesPath);
    return storedBoundingBoxes;
};

const mockGetBoundsPolygons = async () => {
    if (!storedBoundsPolygons) storedBoundsPolygons = await Request.readJson(boundsPolygonsPath);
    return storedBoundsPolygons;
};

const mockGetTheme = async () => {
    if (!storedTheme) storedTheme = await Request.readJson(themePath);
    return storedTheme;
};

const mockGetStyle = async () => {
    if (!storedStyle) storedStyle = await Request.readJson(stylePath);
    return storedStyle;
};

const mockGetContent = async (category, contentType) => {
    switch (contentType) {
        case 'theme':
            return mockGetTheme();
        case 'style':
            return mockGetStyle();
        default:
            return {};
    }
};

// get the format version of the venue data
const mockVenue = async (venueId, format = 'VMS') => {
    possiblyChangeVenue(venueId);
    if (!storedVenue) {
        switch (format) {
            case 'VMS':
                storedVenue = await Request.readJson(venuePath);
                break;
            case 'AI':
                storedVenue = await Request.readJson(aiVenuePath);
                break;
            case 'IMDF':
                storedVenue = await Request.readJson(imdfVenuePath);
                break;
            default:
                storedVenue = await Request.readJson(venuePath);
        }
    }

    return storedVenue;
};

const mockGetEntities = async () => {
    if (!storedEntities) storedEntities = await Request.readJson(entitiesPath);
    return storedEntities;
};

const mockCreatePoi = (venueId, data) => {
    const defaultEntity = {
        uuid: `${venueId}_${data.id}`,
        venueId,

        additionalAttributes: null,
        airlineCode: null,
        airport: null,
        attributes: null,
        categorySearch: null,
        customSearches: null,
        description: null,
        displayProperties: null,
        floor: null,
        gate: null,
        hours: null,
        icon: null,
        images: null,
        logo: null,
        noDirections: null,
        phone: null,
        rawHours: null,
        rotationProperties: null,
        security: null,
        terminal: null,
        url: null,
        urlDisplay: null,
    };

    return Promise.resolve({ ...defaultEntity, ...data });
};

const mockGetContentVenueAccountTree = () => {};

const mockListContent = () => {};

const mockOpenLocation = (venueId, body) => {
    return Promise.resolve(body.entity);
};

const mockUpdatePois = (_venueId, changes) => {
    return Promise.resolve(changes.map(() => Promise.resolve('MOCKED')));
};

const mockListVenuesForAccount = () => Request.readJson('venue-list.json');

const mockGetVenueOrdinals = async () => {
    if (!storedOrdinals) storedOrdinals = await Request.readJson(ordinalsPath);
    return storedOrdinals;
};

const mockGetVenueStructures = async () => {
    if (!storedStructures) storedStructures = await Request.readJson(structuresPath);
    return storedStructures;
};

const mockGetVenueLevels = async () => {
    if (!storedLevels) storedLevels = await Request.readJson(levelsPath);
    return storedLevels;
};

const mockGenerateSpritesheet = () => {};

/*
 * The 'mock' RMS. This will read data from files that are passed in as a file path, or will just mock the request entirely
 */
const MockRms = {
    createPOI: (id, entity) => mockCreatePoi(id, entity),
    getAiJson: (venueId, format) => mockGetAiJson(venueId, format),
    generateSpritesheet: (c, venueId, accountId) => mockGenerateSpritesheet(c, { venue: venueId, account: accountId }),
    getBoundingBoxes: (venueId, opts) => mockGetBoundingBoxes(venueId, opts),
    getBoundsPolygons: (venueId, opts) => mockGetBoundsPolygons(venueId, opts),
    getContentVenueAccountTree: category => mockGetContentVenueAccountTree(category),
    getGeoJson: (id, levelId) => mockGetGeoJson(id, levelId),
    getVenue: (venueId, format) => mockVenue(venueId, format),
    listPOIs: venueId => mockGetEntities(venueId),
    openLocation: (id, obj) => mockOpenLocation(id, obj),
    updatePOIs: (venueId, changes) => mockUpdatePois(venueId, changes),
    getVenueStructures: venueId => mockGetVenueStructures(venueId),
    getVenueLevels: venueId => mockGetVenueLevels(venueId),
    getVenueOrdinals: venueId => mockGetVenueOrdinals(venueId),
    listVenuesForAccount: accountId => mockListVenuesForAccount(accountId, { locale: 'en' }),

    getContent: (contentType, cn, category, venueId, accountId, noExtension) =>
        mockGetContent(category, contentType, cn, { venue: venueId, account: accountId, noExtension }),
    listContent: (contentType, category, venueId, accountId, noExtensions, full) =>
        mockListContent(category, contentType, { venue: venueId, account: accountId, noExtensions, full }),

    venueIdIsMigrated: () => ({ isMigrated: true, isEntityMigrated: true, isFeatureMigrated: false }),
    getVenueFreeze: () => ({ frozen: false }),
    setFilePaths,
};

export { MockRms };
